// Matka King — player page. Three tabs: GAMES (today's markets, play), MY HISAAB (the
// day's legs + the week), RESULTS (the board + charts). Plain HTTP against
// /api/matka_king (no socket); the markets list re-polls every 20 s so countdowns and
// declared results arrive by themselves.
//
// SCREEN (2026-09-19): a list/form page, so it must scroll — NOT ludo.css's global
// `.app-container` and not `.force-portrait`; index.ts declares `fullscreen: true` and
// GameRoute mounts it in the scrolling `.game-fullscreen` frame. Own back button + wallet
// chip in the header; safe-area padding top and bottom.
//
// 2026-09-20 — the owner's review of what players found hard:
//   • the play sheet no longer moves as numbers are added, shows the slip in every tab,
//     shows what is already placed with its total, and stays open after Place with a
//     "Placed ✓" line (MatkaPlaySheet);
//   • the last six weeks' panel/jodi chart sits inside the play sheet under the game name;
//   • My Hisaab shows the totals the old hisaab page showed: Single / Jodi / Patti, the
//     total bet, what came back (with commission when there is one), the difference, and
//     what was added to the wallet;
//   • Results: the pile of chart buttons became one "See chart of…" chooser; the chart
//     itself is the old panel chart with the Panel ⇄ Jodi toggle.
//
// Kept deliberately simple (owner rule: "simple as water"): one screen per job, big taps,
// no jargon — "Open" / "Close" / "Slot 3", "Single / Jodi / Patti".

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '@baaligames/shared-ui/components/Icon';
import { api, ApiError } from '@baaligames/shared-ui/lib/api';
import { useWallet, refreshWallet } from '@baaligames/shared-ui/lib/wallet';
import MatkaPlaySheet, { type SheetItem, type PlayedNumber } from '@baaligames/shared-ui/components/MatkaPlaySheet';
import MatkaPanelChart, { ChartModeToggle, type ChartData, type ChartMode } from '@baaligames/shared-ui/components/MatkaPanelChart';

type Leg = { time: string | null; deadlineMs: number | null; status: string; open: boolean; withinWindow: boolean };
type Slot = Leg & { slot: number; leg: string; result: { pana: string; digit: string } | null };
type Market = {
  id: number; kind: 'double' | 'single'; name: string; slug: string; importance: number; category: 'top' | 'hot' | 'fun' | 'gem'; isHighlight: boolean; playable?: boolean; notice: string | null;
  multipliers: { figure: number; jodi: number; panaSingle: number; panaDouble: number; panaTriple: number; commissionPct: number };
  runsToday: boolean; holiday: boolean;
  openTime?: string; closeTime?: string; closeDate?: string;
  legs?: { open: Leg; close: Leg };
  result?: { openPana: string | null; openDigit: string | null; closePana: string | null; closeDigit: string | null; jodi: string | null; red: boolean } | null;
  slots?: Slot[];
};
type MarketsResp = { date: string; serverMs: number; markets: Market[] };
type StateResp = { enabled: boolean; maintenanceMessage: string | null; wallet: number; bet: { min: number; max: number; maxNumbers: number }; windowHours: number; advanceDays: number; today: string; serverMs: number; validPanas: string[] };
type ResultsResp = { from: string; to: string; doubles: Array<{ market_id: number; name: string; result_date: string; open_pana: string | null; open_digit: string | null; close_pana: string | null; close_digit: string | null; jodi: string | null; red_jodi: number; is_holiday: number }>; singles: Array<{ market_id: number; name: string; result_date: string; slot: number; slot_time: string; pana: string | null; digit: string | null; is_holiday: number }> };

const inr = (n: number) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const CAT_LABEL: Record<string, string> = { top: 'Top games', hot: 'Hot', fun: 'Fun', gem: 'Gem' };
const hhmm = (t?: string | null) => {
  if (!t) return '—';
  const [h, m] = t.split(':').map(Number);
  const ap = h >= 12 ? 'PM' : 'AM';
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${ap}`;
};
const fmtDate = (d?: string | null) => {
  if (!d) return '';
  const t = new Date(`${d}T00:00:00+05:30`);
  return Number.isNaN(t.getTime()) ? d : t.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' });
};
const legLabel = (kind: string, leg: string) => (kind === 'double' ? (leg === 'open' ? 'Open' : 'Close') : `Slot ${leg.replace('s', '')}`);
function countdown(ms: number | null, nowMs: number) {
  if (ms == null) return '';
  const d = Math.max(0, ms - nowMs);
  const h = Math.floor(d / 3600000), m = Math.floor((d % 3600000) / 60000), s = Math.floor((d % 60000) / 1000);
  return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`;
}
const board = (r?: Market['result'] | null) =>
  r ? `${r.openPana ?? '***'}-${r.openDigit ?? '*'}${r.closeDigit ?? '*'}-${r.closePana ?? '***'}` : '***-**-***';
const todayIST = () => new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10);
const playedFromLeg = (d: any): PlayedNumber[] =>
  [...(d?.bets?.figure || []), ...(d?.bets?.jodi || []), ...(d?.bets?.pana || [])].map((b: any) => ({ number: b.number, stake: b.stake, type: b.cls ? 'pana' : (b.number.length === 2 ? 'jodi' : 'figure'), byAgent: !!b.placedBy }));

export default function MatkaKingPage() {
  const navigate = useNavigate();
  const wallet = useWallet();
  const [tab, setTab] = useState<'markets' | 'bets' | 'results'>('markets');
  const [state, setState] = useState<StateResp | null>(null);
  const [markets, setMarkets] = useState<MarketsResp | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  const [play, setPlay] = useState<{ market: Market; leg: string; date: string; played: PlayedNumber[]; chart: ChartData | null } | null>(null);
  const [offset, setOffset] = useState(0);          // serverMs − Date.now()
  const [notice, setNotice] = useState<{ key: string; text: string; tone: 'win' | 'lose' | 'info' } | null>(null);
  const seenRef = useRef<Set<string>>(new Set());
  // Read through a ref so pollDeclared stays referentially stable — otherwise every
  // notice change re-created all three intervals and re-fetched /state.
  const noticeRef = useRef<typeof notice>(null);
  noticeRef.current = notice;
  const [tick, setTick] = useState(Date.now());

  const load = useCallback(async () => {
    try {
      const m = await api.get<MarketsResp>('/matka_king/markets');
      setMarkets(m); setOffset(m.serverMs - Date.now()); setErr(null); setLocked(false);
    } catch (e) {
      if (e instanceof ApiError && (e.status === 403)) setLocked(true);
      else setErr(e instanceof Error ? e.message : 'Could not load markets');
    }
  }, []);
  // Result notices (plan M-70): every 20 s ask which of MY games declared in the last 30 min;
  // show each once per session, worded from my side (won / lost / declared).
  const pollDeclared = useCallback(async () => {
    try {
      const d = await api.get<any>('/matka_king/declared?minutes=30');
      const seen = seenRef.current;
      let stored: string[] = [];
      try { stored = JSON.parse(localStorage.getItem('matka.seen') || '[]'); } catch { /* ignore */ }
      stored.forEach((k) => seen.add(k));
      for (const s of d.settled || []) {
        const key = `s:${s.market_id}:${s.leg}:${s.house_net}`;
        if (seen.has(key)) continue; seen.add(key);
        const net = -Number(s.house_net);
        const name = (d.doubles || []).find((x: any) => x.market_id === s.market_id)?.name || (d.singles || []).find((x: any) => x.market_id === s.market_id)?.name || 'Your game';
        setNotice({ key, tone: net > 0 ? 'win' : net < 0 ? 'lose' : 'info', text: net > 0 ? `${name}: you won ${inr(net)} 🎉` : net < 0 ? `${name}: result declared — ${inr(-net)} lost this time` : `${name}: result declared — even` });
        break;
      }
      if (!noticeRef.current) for (const r of d.doubles || []) {
        const key = `d:${r.market_id}:${r.close_pana ? 'c' : 'o'}`;
        if (seen.has(key)) continue; seen.add(key);
        setNotice({ key, tone: 'info', text: `${r.name}: ${r.close_pana ? `close ${r.close_pana} → ${r.close_digit}, jodi ${r.jodi}` : `open ${r.open_pana} → ${r.open_digit}`}` });
        break;
      }
      try { localStorage.setItem('matka.seen', JSON.stringify([...seen].slice(-200))); } catch { /* ignore */ }
    } catch { /* silent */ }
  }, []);
  useEffect(() => {
    api.get<StateResp>('/matka_king/state').then(setState).catch((e) => { if (e instanceof ApiError && e.status === 403) setLocked(true); });
  }, []);
  useEffect(() => {
    void load(); void pollDeclared();
    const p = setInterval(load, 20000);
    const n = setInterval(pollDeclared, 20000);
    const t = setInterval(() => setTick(Date.now()), 1000);
    return () => { clearInterval(p); clearInterval(n); clearInterval(t); };
  }, [load, pollDeclared]);
  const nowMs = tick + offset;

  /** Open the play sheet for a leg: what is already placed + the last weeks' chart load beside it. */
  const openPlay = (m: Market, leg: string, date: string) => {
    setPlay({ market: m, leg, date, played: [], chart: null });
    api.get<any>(`/matka_king/hisaab/leg?marketId=${m.id}&date=${date}&leg=${leg}`)
      .then((d) => setPlay((p) => p && p.market.id === m.id && p.leg === leg ? { ...p, played: playedFromLeg(d) } : p))
      .catch(() => {});
    api.get<ChartData>(`/matka_king/chart?marketId=${m.id}&months=2`)
      .then((c) => setPlay((p) => p && p.market.id === m.id ? { ...p, chart: c } : p))
      .catch(() => {});
  };
  const reloadPlayed = () => {
    const p = play; if (!p) return;
    api.get<any>(`/matka_king/hisaab/leg?marketId=${p.market.id}&date=${p.date}&leg=${p.leg}`)
      .then((d) => setPlay((q) => q && q.market.id === p.market.id && q.leg === p.leg ? { ...q, played: playedFromLeg(d) } : q))
      .catch(() => {});
  };

  return (
    <div className="min-h-full w-full bg-[#fff8ec] dark:bg-[#1a1208] text-arena-900 dark:text-white" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
     <div className="w-full max-w-lg mx-auto">
      <header className="sticky top-0 z-20 bg-gradient-to-r from-amber-600 to-yellow-800 text-white px-3 pb-2.5 flex items-center gap-2 shadow-md" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.625rem)' }}>
        <button type="button" aria-label="Back" onClick={() => navigate('/games')} className="w-9 h-9 grid place-items-center rounded-full bg-white/15 active:scale-95">
          <Icon name="arrow_back" size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-black text-base leading-tight">Matka King</div>
          <div className="text-[11px] opacity-90">{fmtDate(markets?.date ?? state?.today)}</div>
        </div>
        <button type="button" onClick={() => navigate('/wallet')} className="flex items-center gap-1 rounded-full bg-black/25 px-3 py-1.5 font-mono font-black text-sm active:scale-95">
          <Icon name="account_balance_wallet" size={15} />{wallet.loading ? '…' : inr(wallet.balance)}
        </button>
      </header>

      <nav className="grid grid-cols-3 gap-1 p-2">
        {([['markets', 'Games'], ['bets', 'My Hisaab'], ['results', 'Results']] as const).map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            className={`py-2 rounded-xl text-sm font-bold ${tab === k ? 'bg-amber-600 text-white shadow' : 'bg-white/70 dark:bg-white/10 text-arena-700 dark:text-white/80'}`}>
            {l}
          </button>
        ))}
      </nav>

      {notice && (
        <div className={`mx-2 mt-1 rounded-2xl px-3 py-2 flex items-center gap-2 text-sm font-bold ${notice.tone === 'win' ? 'bg-green-100 dark:bg-green-500/15 text-green-800 dark:text-green-200' : notice.tone === 'lose' ? 'bg-red-100 dark:bg-red-500/10 text-red-800 dark:text-red-200' : 'bg-amber-100 dark:bg-amber-500/10 text-amber-900 dark:text-amber-100'}`}>
          <Icon name={notice.tone === 'win' ? 'celebration' : 'campaign'} size={18} /><span className="flex-1">{notice.text}</span>
          <button type="button" aria-label="Dismiss" onClick={() => { setNotice(null); setTab('bets'); }} className="text-xs underline">see</button>
          <button type="button" aria-label="Close" onClick={() => setNotice(null)}><Icon name="close" size={16} /></button>
        </div>
      )}
      {locked && (
        <div className="m-3 rounded-2xl bg-white dark:bg-white/5 p-4 text-center">
          <div className="text-3xl">🔒</div>
          <div className="font-bold mt-1">Matka King opens soon.</div>
          <div className="text-xs text-arena-500 dark:text-white/60 mt-1">This game is not open to play yet.</div>
        </div>
      )}
      {state && !state.enabled && (
        <div className="m-3 rounded-2xl bg-amber-100 dark:bg-amber-500/10 p-3 text-sm">{state.maintenanceMessage || 'Matka King is offline for a moment.'}</div>
      )}
      {err && <div className="m-3 rounded-xl bg-red-100 dark:bg-red-500/10 text-red-700 dark:text-red-300 p-3 text-sm">{err}</div>}

      {tab === 'markets' && markets && (
        <div className="px-2 pb-8 space-y-3">
          {(['top', 'hot', 'fun', 'gem'] as const).map((cat) => {
            const list = markets.markets.filter((m) => (m.category || 'gem') === cat);
            if (!list.length) return null;
            return (
              <div key={cat} className="space-y-2">
                <div className="px-1 text-[11px] font-black uppercase tracking-wider text-amber-700 dark:text-amber-300">{CAT_LABEL[cat]}</div>
                {list.map((m) => (
                  <MarketCard key={m.id} m={m} nowMs={nowMs} date={markets.date} windowHours={state?.windowHours ?? 3} onPlay={(leg, date) => openPlay(m, leg, date)} />
                ))}
              </div>
            );
          })}
          {!markets.markets.length && <div className="text-center text-sm text-arena-500 py-10">No games today.</div>}
        </div>
      )}
      {tab === 'bets' && <MyHisaab />}
      {tab === 'results' && <ResultsBoard />}

      {play && state && (
        <MatkaPlaySheet market={play.market} leg={play.leg} legLabel={legLabel(play.market.kind, play.leg)} date={play.date}
          deadlineText={`closes in ${countdown(play.market.kind === 'double' ? play.market.legs?.[play.leg as 'open' | 'close']?.deadlineMs ?? null : play.market.slots?.find((s) => s.leg === play.leg)?.deadlineMs ?? null, nowMs)}`}
          rules={{ min: state.bet.min, max: state.bet.max, maxNumbers: state.bet.maxNumbers, validPanas: state.validPanas }}
          played={play.played} chart={play.chart} today={markets?.date ?? state.today}
          parse={(text) => api.post<{ items: SheetItem[]; rejected: Array<{ raw: string; reason: string }>; total: number }>('/matka_king/parse', { text })}
          submit={(items, source) => api.post('/matka_king/bets', { marketId: play.market.id, betDate: play.date, leg: play.leg, items: items.map((i) => ({ number: i.number, stake: i.stake })), source })}
          onClose={() => setPlay(null)}
          onPlaced={() => { void refreshWallet(); void load(); reloadPlayed(); }} />
      )}
     </div>
    </div>
  );
}

function MarketCard({ m, nowMs, date, windowHours, onPlay }: { m: Market; nowMs: number; date: string; windowHours: number; onPlay: (leg: string, date: string) => void }) {
  const closedToday = !m.runsToday || m.holiday;
  return (
    <section className={`rounded-2xl bg-white dark:bg-white/5 ring-1 ring-amber-900/10 dark:ring-white/10 p-3 ${m.isHighlight ? 'shadow-md' : ''}`}>
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-black text-[15px] truncate">{m.name}</div>
          <div className="text-[11px] text-arena-500 dark:text-white/60">
            {m.kind === 'double' ? `Open ${hhmm(m.openTime)} · Close ${hhmm(m.closeTime)}` : `${m.slots?.length ?? 0} slots · ${hhmm(m.slots?.[0]?.time)} – ${hhmm(m.slots?.[m.slots.length - 1]?.time)}`}
          </div>
        </div>
        {m.kind === 'double' && (
          <div className={`font-mono font-black text-lg tracking-wider ${m.result?.red ? 'text-red-600 dark:text-red-400' : 'text-amber-800 dark:text-amber-300'}`}>{board(m.result)}</div>
        )}
      </div>
      {m.notice && <div className="mt-2 text-[11px] rounded-lg bg-amber-50 dark:bg-amber-500/10 px-2 py-1">{m.notice}</div>}
      {m.playable === false ? (
        <div className="mt-2 text-xs font-bold text-arena-500 dark:text-white/50">Results only — no play on this game</div>
      ) : closedToday ? (
        <div className="mt-2 text-xs font-bold text-arena-500 dark:text-white/50">{m.holiday ? 'Holiday today' : 'Closed today'}</div>
      ) : m.kind === 'double' && m.legs ? (
        <div className="mt-2 grid grid-cols-2 gap-2">
          {(['open', 'close'] as const).map((leg) => {
            const L = m.legs![leg];
            const open = L.open && L.withinWindow;
            return (
              <button key={leg} type="button" disabled={!open} onClick={() => onPlay(leg, date)}
                className={`rounded-xl px-2 py-2 text-left ${open ? 'bg-amber-600 text-white active:scale-[.98]' : 'bg-arena-100 dark:bg-white/5 text-arena-500 dark:text-white/40'}`}>
                <div className="text-xs font-black uppercase">{leg}</div>
                <div className="text-[11px]">
                  {L.status !== 'pending' ? 'Result declared' : !L.open ? 'Time over' : open ? `closes in ${countdown(L.deadlineMs, nowMs)}` : `opens in ${countdown((L.deadlineMs ?? 0) - windowHours * 3600000, nowMs)}`}
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="mt-2 flex gap-1.5 overflow-x-auto pb-1">
          {m.slots?.map((s) => (
            <button key={s.slot} type="button" disabled={!s.open} onClick={() => onPlay(s.leg, date)}
              className={`shrink-0 rounded-xl px-2.5 py-1.5 text-center ${s.open ? 'bg-amber-600 text-white' : 'bg-arena-100 dark:bg-white/5 text-arena-500 dark:text-white/40'}`}>
              <div className="text-[10px] font-bold">{hhmm(s.time)}</div>
              <div className="font-mono font-black text-sm">{s.result ? `${s.result.pana}-${s.result.digit}` : s.open ? countdown(s.deadlineMs, nowMs) : '—'}</div>
            </button>
          ))}
        </div>
      )}
      <div className="mt-2 text-[10px] text-arena-400 dark:text-white/40">
        Single ×{m.multipliers.figure}{m.kind === 'double' ? ` · Jodi ×${m.multipliers.jodi}` : ''} · Patti ×{m.multipliers.panaSingle}/{m.multipliers.panaDouble}/{m.multipliers.panaTriple}
        {m.multipliers.commissionPct > 0 ? ` · ${m.multipliers.commissionPct}% back` : ''}
      </div>
    </section>
  );
}

// ─── My Hisaab ───────────────────────────────────────────────────────────────
type TypeTotal = { count: number; played: number; won: number };
type ByType = { figure: TypeTotal; jodi: TypeTotal; pana: TypeTotal };
type DayRow = { marketId: number; game: string; kind: string; leg: string; legLabel: string; bets: number; byAgent: boolean; played: number; won: number; net: number | null; settled: boolean };
type DayResp = { date: string; rows: DayRow[]; totals: { played: number; won: number; net: number; pendingLegs: number; byType?: ByType; commission?: number } };
type LegBet = { id: number; number: string; stake: number; multiplier: number; status: string; payout: number; cls: string | null; placedBy: any };
type LegResp = { game: { name: string; kind: string; leg: string; legLabel: string; date: string }; result: { pana: string; digit: string; jodi: string | null } | null; settled: boolean;
  totals: { played: number; won: number; commission: number; net: number | null; creditedToWallet: number | null; byType?: ByType };
  bets: { figure: LegBet[]; jodi: LegBet[]; pana: LegBet[] }; lines: Array<{ k: string; text: string; amount: number | null }> };

const money = (n: number | null | undefined, signed = true) => n == null ? '—' : (signed && n > 0 ? '+' : n < 0 ? '−' : '') + inr(Math.abs(n));
const tone = (n: number | null | undefined) => (n == null ? 'text-arena-400' : n > 0 ? 'text-green-600 dark:text-green-400' : n < 0 ? 'text-red-500' : '');
const sumBets = (bs: LegBet[]) => ({ count: bs.length, played: bs.reduce((s, b) => s + b.stake, 0), won: bs.reduce((s, b) => s + (b.payout || 0), 0) });
const TYPE_BOX: Array<[keyof ByType, string, string]> = [
  ['figure', 'Single', 'bg-amber-100 dark:bg-amber-500/15 text-amber-900 dark:text-amber-100'],
  ['jodi', 'Jodi', 'bg-violet-100 dark:bg-violet-500/15 text-violet-900 dark:text-violet-100'],
  ['pana', 'Patti', 'bg-sky-100 dark:bg-sky-500/15 text-sky-900 dark:text-sky-100'],
];

/** The three type boxes (count · played · won) + the total bet line — the old hisaab page's FIGURE / JODI / PATTI boxes. */
function TypeBoxes({ byType, played, showWon }: { byType: ByType; played: number; showWon: boolean }) {
  return (
    <div>
      <div className="grid grid-cols-3 gap-1.5">
        {TYPE_BOX.map(([k, label, cls]) => (
          <div key={k} className={`rounded-xl p-2 text-center ${cls}`}>
            <div className="text-[10px] uppercase font-black tracking-wide">{label}</div>
            <div className="text-[10px] opacity-80">{byType[k].count} number{byType[k].count === 1 ? '' : 's'}</div>
            <div className="font-mono font-black">{inr(byType[k].played)}</div>
            {showWon && <div className={`text-[11px] font-mono font-bold ${byType[k].won > 0 ? 'text-green-700 dark:text-green-300' : 'opacity-60'}`}>won {inr(byType[k].won)}</div>}
          </div>
        ))}
      </div>
      <div className="mt-1.5 rounded-xl bg-arena-900 text-white dark:bg-black/40 px-3 py-1.5 flex items-center justify-between">
        <span className="text-xs font-black uppercase">Total bet</span>
        <span className="font-mono font-black text-lg">{inr(played)}</span>
      </div>
    </div>
  );
}

/** My Hisaab (plan M-42/M-40/M-45): one day at a time; every leg opens its own calculation. */
function MyHisaab() {
  const [date, setDate] = useState<string>('');
  const [day, setDay] = useState<DayResp | null>(null);
  const [open, setOpen] = useState<{ row: DayRow; detail: LegResp | null } | null>(null);
  const [week, setWeek] = useState<any | null>(null);
  const load = useCallback((d: string) => {
    api.get<DayResp>(`/matka_king/hisaab/day${d ? `?date=${d}` : ''}`).then((r) => { setDay(r); if (!d) setDate(r.date); }).catch(() => {});
  }, []);
  useEffect(() => { load(''); api.get<any>('/matka_king/hisaab/report').then(setWeek).catch(() => {}); }, [load]);
  const openLeg = (row: DayRow) => {
    setOpen({ row, detail: null });
    api.get<LegResp>(`/matka_king/hisaab/leg?marketId=${row.marketId}&date=${day?.date}&leg=${row.leg}`).then((d) => setOpen({ row, detail: d })).catch(() => setOpen(null));
  };
  const dayWonBets = day ? day.totals.won - (day.totals.commission || 0) : 0;
  return (
    <div className="px-2 pb-8 space-y-2">
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => { const d = addDaysStr(date, -1); setDate(d); load(d); }} className="w-9 h-9 grid place-items-center rounded-full bg-white/70 dark:bg-white/10"><Icon name="chevron_left" size={20} /></button>
        <input type="date" value={date} onChange={(e) => { setDate(e.target.value); load(e.target.value); }} className="flex-1 h-9 rounded-xl px-3 text-sm bg-white/70 dark:bg-white/10 outline-none" />
        <button type="button" onClick={() => { const d = addDaysStr(date, 1); setDate(d); load(d); }} className="w-9 h-9 grid place-items-center rounded-full bg-white/70 dark:bg-white/10"><Icon name="chevron_right" size={20} /></button>
      </div>
      {day && day.rows.length > 0 && (
        <section className="rounded-2xl bg-white dark:bg-white/5 p-3 space-y-2">
          <div className="text-[11px] font-black uppercase tracking-wide text-arena-500 dark:text-white/60">Whole day · {fmtDate(day.date)}</div>
          {day.totals.byType && <TypeBoxes byType={day.totals.byType} played={day.totals.played} showWon />}
          <div className="rounded-xl bg-arena-50 dark:bg-white/5 p-2 text-sm space-y-1">
            <div className="flex justify-between"><span>Came back (winnings{(day.totals.commission || 0) > 0 ? ' + commission' : ''})</span><span className="font-mono font-bold">{inr(day.totals.won)}</span></div>
            {(day.totals.commission || 0) > 0 && <div className="flex justify-between text-[11px] text-arena-500 dark:text-white/60"><span>of which commission</span><span className="font-mono">{inr(day.totals.commission || 0)}</span></div>}
            <div className="flex justify-between"><span>Total bet</span><span className="font-mono font-bold">− {inr(day.totals.played)}</span></div>
            <div className={`flex justify-between border-t border-arena-200 dark:border-white/10 pt-1 font-black ${tone(day.totals.net)}`}><span>{day.totals.net >= 0 ? 'Profit' : 'Loss'} for the day</span><span className="font-mono">{money(day.totals.net)}</span></div>
          </div>
          {day.totals.pendingLegs > 0 && <div className="text-center text-[11px] text-arena-500 dark:text-white/60">{day.totals.pendingLegs} result{day.totals.pendingLegs > 1 ? 's' : ''} still to come — winnings so far {inr(dayWonBets)}</div>}
        </section>
      )}
      {day?.rows.map((r) => (
        <button key={`${r.marketId}-${r.leg}`} type="button" onClick={() => openLeg(r)} className="w-full text-left rounded-2xl bg-white dark:bg-white/5 p-3 active:scale-[.99]">
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <div className="font-black text-[15px] truncate">{r.game} <span className="text-xs font-bold text-arena-500 dark:text-white/60">· {r.legLabel}</span></div>
              <div className="text-[11px] text-arena-500 dark:text-white/60">{r.bets} number{r.bets > 1 ? 's' : ''} · bet {inr(r.played)}{r.settled ? ` · came back ${inr(r.won)}` : ''}{r.byAgent ? ' · some placed by your agent' : ''}{!r.settled ? ' · waiting for result' : ''}</div>
            </div>
            <div className={`font-mono font-black text-lg ${tone(r.net)}`}>{r.settled ? money(r.net) : '…'}</div>
            <Icon name="chevron_right" size={18} className="text-arena-300" />
          </div>
        </button>
      ))}
      {day && !day.rows.length && <div className="text-center text-sm py-8 text-arena-500">Nothing played on {fmtDate(day.date)}.</div>}
      {week && (
        <section className="rounded-2xl bg-white dark:bg-white/5 p-3">
          <div className="font-black text-sm">Last 7 days</div>
          <div className="mt-1 grid grid-cols-3 gap-2 text-center text-xs">
            <div><div className="text-[10px] uppercase font-bold text-arena-400">Bet</div><div className="font-mono font-bold">{inr(week.totals.played)}</div></div>
            <div><div className="text-[10px] uppercase font-bold text-arena-400">Came back</div><div className="font-mono font-bold">{inr(week.totals.won + week.totals.commission)}</div></div>
            <div><div className="text-[10px] uppercase font-bold text-arena-400">Net</div><div className={`font-mono font-bold ${tone(week.totals.net)}`}>{money(week.totals.net)}</div></div>
          </div>
          <div className="mt-2 flex items-end gap-1 h-14">
            {week.timeline.map((d: any) => { const max = Math.max(1, ...week.timeline.map((x: any) => Math.abs(x.net))); const h = Math.max(3, Math.round(Math.abs(d.net) / max * 48)); return (
              <div key={d.date} className="flex-1 flex flex-col items-center justify-end" title={`${d.date}: ${money(d.net)}`}>
                <div className={`w-full rounded-t ${d.net >= 0 ? 'bg-green-500/70' : 'bg-red-500/70'}`} style={{ height: h }} />
                <div className="text-[9px] text-arena-400">{d.date.slice(8)}</div>
              </div>); })}
            {!week.timeline.length && <div className="text-[11px] text-arena-400">No settled play in the last 7 days.</div>}
          </div>
          <div className="mt-2 text-[11px] text-arena-500 dark:text-white/60">Wallet: opening {inr(week.wallet.opening)} → now {inr(week.wallet.now)}{week.transactions.length ? ` · ${week.transactions.length} recharge/withdrawal entries` : ''}</div>
        </section>
      )}

      {open && (
        <div className="fixed inset-0 z-40 flex flex-col justify-end bg-black/50" onClick={() => setOpen(null)}>
          <div className="bg-white dark:bg-[#24190b] rounded-t-3xl p-4 max-h-[90dvh] overflow-y-auto" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 2rem)' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <div className="flex-1"><div className="font-black">{open.row.game} · {open.row.legLabel}</div><div className="text-[11px] text-arena-500 dark:text-white/60">{fmtDate(day?.date)}</div></div>
              <button type="button" onClick={() => setOpen(null)} className="w-9 h-9 grid place-items-center rounded-full bg-arena-100 dark:bg-white/10"><Icon name="close" size={20} /></button>
            </div>
            {!open.detail ? <div className="py-8 text-center text-sm text-arena-500">Loading…</div> : <LegHisaab d={open.detail} />}
          </div>
        </div>
      )}
    </div>
  );
}

/** One leg, laid out like the old hisaab page: type boxes → total bet → result → what came back → profit/loss → added to wallet. */
function LegHisaab({ d }: { d: LegResp }) {
  const byType: ByType = d.totals.byType ?? { figure: sumBets(d.bets.figure), jodi: sumBets(d.bets.jodi), pana: sumBets(d.bets.pana) };
  const wonBets = byType.figure.won + byType.jodi.won + byType.pana.won;
  const commission = d.totals.commission || 0;
  const cameBack = wonBets + commission;
  const net = d.settled ? cameBack - d.totals.played : null;
  const resultText = d.result
    ? (d.game.kind === 'double' ? (d.game.leg === 'open' ? `Open ${d.result.pana} → ${d.result.digit}` : `Close ${d.result.pana} → ${d.result.digit}${d.result.jodi ? `, jodi ${d.result.jodi}` : ''}`) : `Result ${d.result.pana} → ${d.result.digit}`)
    : null;
  const row = (label: string, value: string, cls = '') => <div className={`flex justify-between gap-2 ${cls}`}><span>{label}</span><span className="font-mono font-bold whitespace-nowrap">{value}</span></div>;
  return (
    <div className="mt-3 space-y-3">
      <TypeBoxes byType={byType} played={d.totals.played} showWon={d.settled} />

      <div className="space-y-1.5">
        {(['figure', 'jodi', 'pana'] as const).map((t) => d.bets[t].length ? (
          <div key={t}>
            <div className="text-[10px] uppercase font-bold text-arena-400">{t === 'figure' ? 'Single' : t === 'jodi' ? 'Jodi' : 'Patti'} — {d.bets[t].length}</div>
            <div className="flex flex-wrap gap-1.5 mt-0.5">
              {d.bets[t].map((b) => (
                <span key={b.id} className={`rounded-lg px-2 py-1 text-xs font-mono ${b.status === 'won' ? 'bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-300 font-bold' : b.status === 'lost' ? 'bg-arena-100 dark:bg-white/5 text-arena-500 dark:text-white/50 line-through' : 'bg-amber-50 dark:bg-amber-500/10'}`}>
                  <b>{b.number}</b> {inr(b.stake)}{b.status === 'won' ? ` → ${inr(b.payout)}` : ''}{b.placedBy ? ' · by agent' : ''}
                </span>))}
            </div>
          </div>) : null)}
      </div>

      <div className={`rounded-xl px-3 py-2 text-sm font-black text-center ${d.result ? 'bg-amber-100 dark:bg-amber-500/15' : 'bg-arena-100 dark:bg-white/5 text-arena-500 dark:text-white/60'}`}>
        {resultText ?? 'Result not declared yet — nothing is settled.'}
      </div>

      {d.settled && (
        <>
          <div className="rounded-xl bg-green-50 dark:bg-green-500/10 p-3 text-sm space-y-1">
            <div className="text-[10px] uppercase font-black tracking-wide text-green-800 dark:text-green-200">⇣ What came back ⇣</div>
            {row('Single won', inr(byType.figure.won))}
            {row('Jodi won', inr(byType.jodi.won))}
            {row('Patti won', inr(byType.pana.won))}
            {commission > 0 && row('Commission back', inr(commission))}
            {row('Total came back', inr(cameBack), 'border-t border-green-200 dark:border-green-500/20 pt-1 font-black')}
          </div>
          <div className={`rounded-xl p-3 text-sm space-y-1 ${net != null && net >= 0 ? 'bg-emerald-100 dark:bg-emerald-500/15' : 'bg-red-50 dark:bg-red-500/10'}`}>
            <div className="text-[10px] uppercase font-black tracking-wide">⇣ {net != null && net >= 0 ? 'Profit' : 'Loss'} ⇣</div>
            {row('Came back', inr(cameBack))}
            {row('Total bet', `− ${inr(d.totals.played)}`)}
            {row(net != null && net >= 0 ? 'Profit' : 'Loss', money(net), `border-t border-black/10 dark:border-white/10 pt-1 font-black text-base ${tone(net)}`)}
          </div>
          <div className="rounded-xl bg-arena-900 text-white dark:bg-black/40 px-3 py-2 text-sm flex justify-between items-center">
            <span className="font-bold">Added to your wallet</span>
            <span className="font-mono font-black text-lg">{inr(d.totals.creditedToWallet ?? cameBack)}</span>
          </div>
        </>
      )}
    </div>
  );
}

function addDaysStr(d: string, n: number) {
  if (!d) return d;
  const t = new Date(`${d}T00:00:00+05:30`); t.setDate(t.getDate() + n);
  return t.toISOString().slice(0, 10);
}

// ─── Results + charts ────────────────────────────────────────────────────────
/** The full chart (plan M-71): the old panel chart, every week on record, Panel ⇄ Jodi. */
function ChartView({ marketId, name, onBack }: { marketId: number; name: string; onBack: () => void }) {
  const [c, setC] = useState<ChartData | null>(null);
  const [mode, setMode] = useState<ChartMode>(() => { try { return localStorage.getItem('matka.chartMode') === 'jodi' ? 'jodi' : 'panel'; } catch { return 'panel'; } });
  useEffect(() => { setC(null); api.get<ChartData>(`/matka_king/chart?marketId=${marketId}&months=6`).then(setC).catch(() => {}); }, [marketId]);
  return (
    <div className="px-2 pb-8 space-y-2">
      <div className="flex items-center gap-2">
        <button type="button" onClick={onBack} className="w-9 h-9 grid place-items-center rounded-full bg-white/70 dark:bg-white/10"><Icon name="arrow_back" size={20} /></button>
        <div className="flex-1 min-w-0"><div className="font-black truncate">{name}</div><div className="text-[11px] text-arena-500 dark:text-white/60">{mode === 'panel' ? 'Panel chart' : 'Jodi chart'} · last 6 months</div></div>
        <ChartModeToggle mode={mode} onMode={(m) => { setMode(m); try { localStorage.setItem('matka.chartMode', m); } catch { /* ignore */ } }} />
      </div>
      <div className="rounded-2xl bg-white dark:bg-white/5 p-2">
        <MatkaPanelChart data={c} mode={mode} today={todayIST()} scrollToLatest={false} />
        <div className="text-[10px] text-arena-500 dark:text-white/50 mt-1">{mode === 'panel' ? 'Each cell: open patti · jodi · close patti.' : 'Jodi only.'} Red = a double or cut jodi. The number in the left column is how many different figures came that week.</div>
      </div>
    </div>
  );
}

function ResultsBoard() {
  const [r, setR] = useState<ResultsResp | null>(null);
  const [days, setDays] = useState(7);
  const [chart, setChart] = useState<{ id: number; name: string } | null>(null);
  useEffect(() => { api.get<ResultsResp>(`/matka_king/results?days=${days}`).then(setR).catch(() => {}); }, [days]);
  if (chart) return <ChartView marketId={chart.id} name={chart.name} onBack={() => setChart(null)} />;
  if (!r) return <div className="text-center text-sm py-10 text-arena-500">Loading…</div>;
  const dates = [...new Set([...r.doubles.map((x) => x.result_date), ...r.singles.map((x) => x.result_date)])].sort().reverse();
  const gameMap = new Map<number, string>();
  for (const x of r.doubles) gameMap.set(x.market_id, x.name);
  for (const x of r.singles) gameMap.set(x.market_id, x.name);
  const games: Array<{ id: number; name: string }> = [...gameMap.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  return (
    <div className="px-2 pb-8 space-y-2">
      <section className="rounded-2xl bg-white dark:bg-white/5 p-2 flex items-center gap-2">
        <Icon name="grid_on" size={18} className="text-amber-700 dark:text-amber-300 shrink-0" />
        <select value="" onChange={(e) => { const g = games.find((x) => String(x.id) === e.target.value); if (g) setChart(g); }}
          className="flex-1 h-10 rounded-xl px-2 text-sm font-bold bg-arena-50 dark:bg-white/10 outline-none">
          <option value="">See the chart of a game…</option>
          {games.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      </section>
      <div className="flex items-center gap-1">
        {[7, 15, 30].map((d) => <button key={d} type="button" onClick={() => setDays(d)} className={`px-3 py-1.5 rounded-xl text-xs font-bold ${days === d ? 'bg-amber-600 text-white' : 'bg-white/70 dark:bg-white/10'}`}>Last {d} days</button>)}
      </div>
      {dates.map((d) => (
        <section key={d} className="rounded-2xl bg-white dark:bg-white/5 p-3">
          <div className="font-black text-sm mb-1">{fmtDate(d)}</div>
          {r.doubles.filter((x) => x.result_date === d).map((x) => (
            <button key={`d${x.market_id}`} type="button" onClick={() => setChart({ id: x.market_id, name: x.name })} className="w-full flex items-center gap-2 py-1 text-sm text-left active:opacity-70">
              <span className="flex-1 truncate">{x.name}</span>
              <span className={`font-mono font-black tracking-wider ${x.red_jodi ? 'text-red-600 dark:text-red-400' : ''}`}>
                {x.is_holiday ? 'holiday' : `${x.open_pana ?? '***'}-${x.open_digit ?? '*'}${x.close_digit ?? '*'}-${x.close_pana ?? '***'}`}
              </span>
              <Icon name="chevron_right" size={16} className="text-arena-300" />
            </button>
          ))}
          {[...new Set(r.singles.filter((x) => x.result_date === d).map((x) => x.market_id))].map((mid) => {
            const rows = r.singles.filter((x) => x.result_date === d && x.market_id === mid);
            return (
              <button key={`s${mid}`} type="button" onClick={() => setChart({ id: mid, name: rows[0].name })} className="w-full py-1 text-left active:opacity-70">
                <div className="flex items-center gap-2 text-sm"><span className="flex-1 truncate">{rows[0].name}</span><Icon name="chevron_right" size={16} className="text-arena-300" /></div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {rows.map((x) => (
                    <span key={x.slot} className="rounded-lg bg-amber-50 dark:bg-white/10 px-1.5 py-0.5 font-mono text-xs">
                      <span className="text-arena-400">{hhmm(x.slot_time)} </span><b>{x.pana ? `${x.pana}-${x.digit}` : '—'}</b>
                    </span>
                  ))}
                </div>
              </button>
            );
          })}
        </section>
      ))}
      {!dates.length && <div className="text-center text-sm py-8 text-arena-500">No results yet.</div>}
    </div>
  );
}
